// Available distribution destinations, derived from "Marketplace Pipeline -
// Integrations.csv". The pickable universe is the 3rd-party (3P) destinations
// the TransUnion and Experian marketplaces carry (1P-only integrations are
// excluded).
//
// Each destination is reached one of two ways:
//   - `direct: true`  — we have (or are building) a direct integration with the
//     platform. Preferred: when present it's the only route we offer.
//   - `indirectVia`   — the marketplace(s) that carry the destination as 3P, used
//     when there's no direct route. Two entries means both carry it, so the user
//     gets to choose; this is preserved rather than collapsed.
// Framework-agnostic by the lib/** layer rule.

export type MarketplaceProvider = 'transunion' | 'experian'

// Display label for a marketplace provider (single source of truth). Used where
// the specific provider matters — i.e. entering that account's API credentials.
export const MARKETPLACE_PROVIDER_LABEL: Record<MarketplaceProvider, string> = {
    transunion: 'TransUnion',
    experian: 'Experian',
}

// TEMPORARY — demo only: don't show the real marketplace name to the buyer.
// The route/delivery UI displays these neutral, industry-flavored aliases instead
// of the provider names. They're kept distinct (one per provider) so the carrier
// picker in the Configure modal still has two tell-apart options. Sibling of (not
// a replacement for) MARKETPLACE_PROVIDER_LABEL above, which is still used for
// credential entry. To surface real provider names in the route UI again, delete
// this and point the route-display call sites back at MARKETPLACE_PROVIDER_LABEL.
export const MARKETPLACE_DELIVERY_LABEL: Record<MarketplaceProvider, string> = {
    transunion: 'Distribution Network',
    experian: 'Delivery Network',
}

// How "success" is measured per kind of destination. Lives here (with the
// catalog) as the single source of truth for destination type. Coarse for now;
// a richer taxonomy is a deferred TODO (notes/refactor-todos.md).
export type DestinationType = 'dsp' | 'identity' | 'data-delivery'

// Display label for a destination type (single source of truth).
export const DESTINATION_TYPE_LABEL: Record<DestinationType, string> = {
    dsp: 'DSP',
    identity: 'Identity',
    'data-delivery': 'Data delivery',
}

export interface MarketplaceDestination {
    name: string
    // We have (or are building) a direct integration with this platform.
    direct: boolean
    // Marketplace(s) carrying this destination as a 3P integration — the
    // fallback route(s) when there's no direct integration.
    indirectVia: MarketplaceProvider[]
}

// The type of a destination, looked up from the catalog by name. The catalog has
// no per-entry type yet (taxonomy deferred), so every catalog option — and any
// unknown name — reads as 'dsp' (match-rate success). The data-delivery branch
// in consumers stays dormant until the catalog gains real types.
export function destinationType(_name: string): DestinationType {
    return 'dsp'
}

export const marketplaceDestinations: MarketplaceDestination[] = [
    { name: 'Acuity Ads', direct: false, indirectVia: ['transunion'] },
    { name: 'Amazon', direct: false, indirectVia: ['experian'] },
    { name: 'Amperwave', direct: false, indirectVia: ['transunion'] },
    { name: 'ArcSpan', direct: false, indirectVia: ['experian'] },
    {
        name: 'Audigent',
        direct: false,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'Beachfront', direct: false, indirectVia: ['transunion'] },
    { name: 'Beeswax', direct: false, indirectVia: ['transunion', 'experian'] },
    { name: 'Cadent', direct: false, indirectVia: ['experian'] },
    { name: 'Consumable', direct: false, indirectVia: ['transunion'] },
    { name: 'Dax', direct: false, indirectVia: ['experian'] },
    { name: 'DeepIntent', direct: false, indirectVia: ['experian'] },
    { name: 'DirecTV', direct: false, indirectVia: ['experian'] },
    { name: 'Dish Media', direct: false, indirectVia: ['experian'] },
    { name: 'Fox', direct: false, indirectVia: ['transunion'] },
    { name: 'Fox News', direct: false, indirectVia: ['experian'] },
    { name: 'Fox Sports', direct: false, indirectVia: ['experian'] },
    { name: 'Frameplay', direct: false, indirectVia: ['transunion'] },
    {
        name: 'Freewheel',
        direct: false,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'Google Ad Manager', direct: false, indirectVia: ['experian'] },
    {
        name: 'Google DV360',
        direct: false,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'Jamloop', direct: false, indirectVia: ['transunion'] },
    {
        name: 'LG Ad Solutions',
        direct: false,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'Madhive', direct: false, indirectVia: ['experian'] },
    { name: 'Magnite', direct: false, indirectVia: ['experian'] },
    { name: 'Media.net', direct: false, indirectVia: ['experian'] },
    { name: 'Meta', direct: false, indirectVia: ['experian'] },
    { name: 'NBC Universal', direct: false, indirectVia: ['experian'] },
    { name: 'Nexxen', direct: false, indirectVia: ['transunion', 'experian'] },
    { name: 'OpenAP', direct: false, indirectVia: ['experian'] },
    { name: 'OpenX', direct: false, indirectVia: ['experian'] },
    { name: 'Optimum Media', direct: false, indirectVia: ['experian'] },
    { name: 'OutsideTV', direct: false, indirectVia: ['transunion'] },
    { name: 'Pandora', direct: false, indirectVia: ['transunion'] },
    { name: 'Paramount', direct: false, indirectVia: ['experian'] },
    { name: 'Pinterest', direct: false, indirectVia: ['experian'] },
    {
        name: 'Pontiac Intelligence',
        direct: false,
        indirectVia: ['transunion'],
    },
    { name: 'PubMatic', direct: false, indirectVia: ['experian'] },
    { name: 'Samsung', direct: false, indirectVia: ['experian'] },
    {
        name: 'Simulmedia',
        direct: false,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'SpotX', direct: false, indirectVia: ['transunion'] },
    {
        name: 'SpringServe',
        direct: false,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'Stackadapt', direct: false, indirectVia: ['experian'] },
    { name: 'Targetspot', direct: false, indirectVia: ['transunion'] },
    {
        name: 'The Trade Desk',
        direct: true,
        indirectVia: ['transunion', 'experian'],
    },
    { name: 'TikTok', direct: false, indirectVia: ['experian'] },
    { name: 'Tremor Video', direct: false, indirectVia: ['transunion'] },
    { name: 'TripleLift', direct: false, indirectVia: ['experian'] },
    { name: 'Triton', direct: false, indirectVia: ['transunion'] },
    { name: 'Tubi', direct: false, indirectVia: ['transunion'] },
    { name: 'Undertone', direct: false, indirectVia: ['transunion'] },
    { name: 'Viant', direct: false, indirectVia: ['transunion', 'experian'] },
    { name: 'Vizio', direct: false, indirectVia: ['transunion', 'experian'] },
    { name: 'Weather Group', direct: false, indirectVia: ['transunion'] },
    { name: 'Xandr', direct: false, indirectVia: ['transunion'] },
    { name: 'Xumo', direct: false, indirectVia: ['transunion'] },
    { name: 'Yahoo', direct: false, indirectVia: ['transunion', 'experian'] },
    { name: 'Yieldmo', direct: false, indirectVia: ['experian'] },
]

// --- Routing ---
//
// From the user's perspective they pick the final destination first; we then
// resolve how to get there. The rule: a direct integration always wins (when one
// exists it's the only route offered); otherwise the destination is reached
// through the marketplace(s) that carry it. A choice is presented to the user
// only when there's no direct route AND more than one marketplace carries it.
export type DestinationRoute =
    | { kind: 'direct' }
    | { kind: 'indirect'; via: MarketplaceProvider }

export function destinationRoutes(
    destination: MarketplaceDestination
): DestinationRoute[] {
    if (destination.direct) return [{ kind: 'direct' }]
    return destination.indirectVia.map((via) => ({ kind: 'indirect', via }))
}

// The route taken when no choice is required (direct, or the sole marketplace).
export function defaultDestinationRoute(
    destination: MarketplaceDestination
): DestinationRoute {
    return destinationRoutes(destination)[0]
}

// True only when the user must pick how to reach the destination (no direct
// integration and more than one marketplace carries it).
export function requiresRouteChoice(
    destination: MarketplaceDestination
): boolean {
    return destinationRoutes(destination).length > 1
}

// --- One-time configuration (collected lazily) ---
//
// We never make the user configure anything upfront. Each piece of one-time
// setup is collected the first time it's actually needed (at activation), then
// remembered. There are three pieces:
//
//   1. Marketplace connection — credentials for a TransUnion / Experian account.
//      Collected once, then shared by every destination reached through it.
//   2. Direct connection — credentials for a direct platform (only The Trade Desk
//      today). For a direct route the seat lives here; there's no separate setup.
//   3. Destination setup — for a marketplace route, the platform account the
//      marketplace should deliver to (advertiser / seat ID). Stored once per
//      destination, then reused.

export interface ConfigField {
    key: string
    label: string
    type: 'text' | 'secret'
    placeholder?: string
}

// Credentials for a marketplace account (shared across all of its destinations).
export const marketplaceConnectionFields: Record<
    MarketplaceProvider,
    ConfigField[]
> = {
    transunion: [
        { key: 'accountId', label: 'TruAudience account ID', type: 'text' },
        { key: 'apiToken', label: 'API token', type: 'secret' },
    ],
    experian: [
        { key: 'accountId', label: 'Experian account ID', type: 'text' },
        { key: 'apiToken', label: 'API token', type: 'secret' },
    ],
}

// Credentials for a direct platform integration, keyed by destination name. Only
// The Trade Desk today; others fall back to a generic seat + key pair.
const DIRECT_CONNECTION_FIELDS: Record<string, ConfigField[]> = {
    'The Trade Desk': [
        { key: 'partnerId', label: 'Partner ID', type: 'text' },
        { key: 'advertiserSeatId', label: 'Advertiser seat ID', type: 'text' },
        { key: 'encryptionKey', label: 'Encryption key', type: 'secret' },
    ],
}

export function directConnectionFields(
    destination: MarketplaceDestination
): ConfigField[] {
    return (
        DIRECT_CONNECTION_FIELDS[destination.name] ?? [
            {
                key: 'advertiserSeatId',
                label: 'Advertiser seat ID',
                type: 'text',
            },
            { key: 'apiKey', label: 'API key', type: 'secret' },
        ]
    )
}

// The per-destination platform account a marketplace delivers into.
export function destinationSetupFields(
    destination: MarketplaceDestination
): ConfigField[] {
    return [
        {
            key: 'platformAccountId',
            label: `${destination.name} advertiser / seat ID`,
            type: 'text',
        },
    ]
}

// A partially-filled config: field key → value. Absent or blank values are
// treated as unfilled. We persist whatever the user has entered so far — even an
// empty or half-done slot — and never store a "configured" flag. Completeness is
// always derived against the live field schema below, so an upstream change (a
// new required field, or a route that flips to direct) automatically resurfaces
// a previously-finished config as needing attention.
export type ConfigValues = Record<string, string>

// One slot of one-time config for a route, annotated with what's saved and
// what's still missing. `missing` empty means the slot is complete.
interface ConfigSlotBase {
    fields: ConfigField[]
    values: ConfigValues
    missing: ConfigField[]
}
export type ConfigStep =
    | (ConfigSlotBase & {
          kind: 'marketplace-connection'
          provider: MarketplaceProvider
      })
    | (ConfigSlotBase & { kind: 'direct-connection'; destination: string })
    | (ConfigSlotBase & {
          kind: 'destination-setup'
          destination: string
          via: MarketplaceProvider
      })

// Saved config values per slot, wired by callers to the stored state. Returning
// `undefined` (or a partial object) is fine — missing fields are computed.
export interface SavedConfig {
    marketplace: (provider: MarketplaceProvider) => ConfigValues | undefined
    direct: (destinationName: string) => ConfigValues | undefined
    destination: (
        destinationName: string,
        via: MarketplaceProvider
    ) => ConfigValues | undefined
}

// Required fields with no (non-blank) saved value.
function missingFields(
    fields: ConfigField[],
    values: ConfigValues | undefined
): ConfigField[] {
    return fields.filter((f) => !values?.[f.key]?.trim())
}

// Every one-time config slot a route touches, each annotated with saved values
// and outstanding fields. Use this to render the setup flow (it shows complete
// slots too, so the user can review/update them) and to report completeness.
export function routeConfigSteps(
    destination: MarketplaceDestination,
    route: DestinationRoute,
    saved: SavedConfig
): ConfigStep[] {
    if (route.kind === 'direct') {
        const fields = directConnectionFields(destination)
        const values = saved.direct(destination.name) ?? {}
        return [
            {
                kind: 'direct-connection',
                destination: destination.name,
                fields,
                values,
                missing: missingFields(fields, values),
            },
        ]
    }

    const connFields = marketplaceConnectionFields[route.via]
    const connValues = saved.marketplace(route.via) ?? {}
    const setupFields = destinationSetupFields(destination)
    const setupValues = saved.destination(destination.name, route.via) ?? {}
    return [
        {
            kind: 'marketplace-connection',
            provider: route.via,
            fields: connFields,
            values: connValues,
            missing: missingFields(connFields, connValues),
        },
        {
            kind: 'destination-setup',
            destination: destination.name,
            via: route.via,
            fields: setupFields,
            values: setupValues,
            missing: missingFields(setupFields, setupValues),
        },
    ]
}

// The slots still needing input before this route is fully set up — the lazy
// driver. Empty means ready to activate; non-empty drives both the just-in-time
// setup prompt and the "needs config" notice on the distribution page.
export function pendingConfig(
    destination: MarketplaceDestination,
    route: DestinationRoute,
    saved: SavedConfig
): ConfigStep[] {
    return routeConfigSteps(destination, route, saved).filter(
        (step) => step.missing.length > 0
    )
}

// Whether a route is fully configured (nothing left to fill in).
export function isRouteConfigured(
    destination: MarketplaceDestination,
    route: DestinationRoute,
    saved: SavedConfig
): boolean {
    return pendingConfig(destination, route, saved).length === 0
}

// Whether a destination's *connection* is set up (independent of any single
// delivery's per-destination setup): the direct connection for a direct
// platform, or at least one carrying marketplace account for an indirect one.
// Destinations not in the catalog have no connection to configure → ready.
export function destinationConnectionReady(
    name: string,
    saved: SavedConfig
): boolean {
    const destination = findDestination(name)
    if (!destination) return true
    if (destination.direct) {
        return (
            missingFields(
                directConnectionFields(destination),
                saved.direct(name)
            ).length === 0
        )
    }
    return destination.indirectVia.some(
        (via) =>
            missingFields(
                marketplaceConnectionFields[via],
                saved.marketplace(via)
            ).length === 0
    )
}

// Look up a destination by its display name (e.g., to map a distribution's
// platform back onto the routing/config model).
export function findDestination(
    name: string
): MarketplaceDestination | undefined {
    return marketplaceDestinations.find((d) => d.name === name)
}

// Points at one config slot for reads/writes (mirrors the SavedConfig accessors).
// Callers persist partial values against one of these.
export type ConfigSlotRef =
    | { kind: 'marketplace'; provider: MarketplaceProvider }
    | { kind: 'direct'; destination: string }
    | { kind: 'destination'; destination: string; via: MarketplaceProvider }
